import Paper from '@/components/Paper'
import type { DataTableColumn } from '@/components/ui/data-table-customized'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import SampleAttachment from '@/modules/Contract/modules/Sample/components/SampleAttachment'
import { getMappingContractSampleForProduct } from '@/modules/Contract/modules/Sample/services'
import { SampleContract } from '@/modules/Contract/modules/Sample/types'
import { getSuggestionSampleContract } from '@/modules/Contract/services/customer'
import { formatDateWithFormatter } from '@/utils/function'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Chip, Typogaphy } from 'vnetwork-ui'

interface SuggestionsProps {
    contractID: number
}

const Suggestions = ({ contractID }: SuggestionsProps) => {
    const { t } = useTranslation()

    const { data: mappingData } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CONTRACTS_SAMPLES_MAPPING],
        queryFn: getMappingContractSampleForProduct,
    })

    const { data: suggestionsData, isFetching } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.CONTRACTS_SUGGESTIONS, contractID],
            queryFn: () => getSuggestionSampleContract({ contractID }),
        },
        { showLoading: true, showError: true }
    )

    const mappingOptions = mappingData || []

    const columns = useMemo<DataTableColumn<SampleContract>[]>(
        () => [
            {
                key: 'contract_number_id',
                title: t('contract_number'),
                width: 180,
                render: (_, row) => (
                    <div>
                        <Typogaphy className="font-medium text-sm text-primary">
                            {row.contract_number_id}
                        </Typogaphy>
                        <Typogaphy className="text-xs text-gray-500">
                            {row.contract_key}
                        </Typogaphy>
                    </div>
                ),
            },
            {
                key: 'contract_type' as any,
                title: t('contract_type'),
                width: 120,
                render: (_, row) => {
                    const isSLA = row.sla_enable
                    const isNDA = row.nda_enable
                    const label = isSLA
                        ? t('samples.sla')
                        : isNDA
                          ? t('samples.nda')
                          : t('samples.sample')
                    const className = isSLA
                        ? 'bg-primary/10 text-primary'
                        : isNDA
                          ? 'bg-success/10 text-success'
                          : 'bg-gray-100 text-gray-500'
                    return (
                        <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${className}`}
                        >
                            {label}
                        </span>
                    )
                },
            },
            {
                key: 'product_group_name',
                title: t('product_group'),
                width: 150,
                render: (value) => {
                    if (!value) return '-'
                    const label = (mappingOptions as any[])?.find(
                        (item: { value: string; label: string }) =>
                            item.value === value
                    )?.label
                    return label ? (
                        <Chip size="small" variant="outlined" label={label} />
                    ) : (
                        <span>{value as string}</span>
                    )
                },
            },
            {
                key: 'contract_note',
                title: t('contract_note'),
                render: (value) => (
                    <Typogaphy className="text-xs text-gray-600 line-clamp-2">
                        {(value as string) || '-'}
                    </Typogaphy>
                ),
            },
            {
                key: 'attachments',
                title: t('attachments'),
                width: 200,
                render: (_, row) => {
                    if (!row.attachments || row.attachments.length === 0) {
                        return <span className="text-gray-400 text-xs">-</span>
                    }
                    return (
                        <div className="flex gap-1 items-center">
                            {row.attachments
                                .slice(0, 2)
                                .map((attachment, index) => (
                                    <SampleAttachment
                                        key={index}
                                        attachment={attachment}
                                    />
                                ))}
                            {row.attachments.length > 2 && (
                                <span className="text-xs text-gray-500">
                                    +{row.attachments.length - 2}
                                </span>
                            )}
                        </div>
                    )
                },
            },
            {
                key: 'updated_at',
                title: t('lasted_update'),
                width: 150,
                render: (value) => (
                    <Typogaphy className="text-xs text-gray-500">
                        {formatDateWithFormatter(
                            value as string,
                            'DD/MM/YYYY HH:mm'
                        )}
                    </Typogaphy>
                ),
            },
        ],
        [t, mappingOptions]
    )

    const rows = suggestionsData?.results || []

    if (!isFetching && rows.length === 0) return null

    return (
        <Paper className="mt-2">
            <Typogaphy className="mb-2 font-medium text-primary capitalize">
                {t('samples.suggestions')}
            </Typogaphy>
            <DataTableCustomized<SampleContract>
                columns={columns}
                rows={rows}
                rowKey={(row) => row.contract_id}
                isFetching={isFetching}
                emptyMessage={t('noData', 'Không có dữ liệu')}
                bordered
            />
        </Paper>
    )
}

export default Suggestions
